import { __awaiter } from "tslib";
import { AnchorProvider, BorshAccountsCoder, Program, utils, } from "@project-serum/anchor";
import { NAMESPACES_IDL, NAMESPACES_PROGRAM_ID } from ".";
import { findClaimRequestId, findGlobalContextId, findNameEntryId, findNamespaceId, findReverseEntryId, } from "./pda";
export function getNamespaceByName(connection, namespaceName) {
    return __awaiter(this, void 0, void 0, function* () {
        const [namespaceId] = yield findNamespaceId(namespaceName);
        return getNamespace(connection, namespaceId);
    });
}
export function getNamespace(connection, namespaceId) {
    return __awaiter(this, void 0, void 0, function* () {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const provider = new AnchorProvider(connection, null, {});
        const namespacesProgram = new Program(NAMESPACES_IDL, NAMESPACES_PROGRAM_ID, provider);
        const parsed = yield namespacesProgram.account.namespace.fetch(namespaceId);
        return {
            parsed,
            pubkey: namespaceId,
        };
    });
}
export function getGlobalContext(connection) {
    return __awaiter(this, void 0, void 0, function* () {
        const [globalContextId] = yield findGlobalContextId();
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const provider = new AnchorProvider(connection, null, {});
        const namespacesProgram = new Program(NAMESPACES_IDL, NAMESPACES_PROGRAM_ID, provider);
        const parsed = yield namespacesProgram.account.globalContext.fetch(globalContextId);
        return {
            parsed,
            pubkey: globalContextId,
        };
    });
}
export function getAllNamespaces(connection) {
    return __awaiter(this, void 0, void 0, function* () {
        const programAccounts = yield connection.getProgramAccounts(NAMESPACES_PROGRAM_ID, {
            filters: [
                {
                    memcmp: {
                        offset: 0,
                        bytes: utils.bytes.bs58.encode(BorshAccountsCoder.accountDiscriminator("namespace")),
                    },
                },
            ],
        });
        const namespaces = [];
        const coder = new BorshAccountsCoder(NAMESPACES_IDL);
        programAccounts.forEach((account) => {
            try {
                const namespace = coder.decode("namespace", account.account.data);
                namespaces.push(Object.assign(Object.assign({}, account), { parsed: namespace }));
            }
            catch (e) {
                console.log(`Failed to decode namespace`);
            }
        });
        return namespaces.sort((a, b) => a.pubkey.toBase58().localeCompare(b.pubkey.toBase58()));
    });
}
export function getNameEntry(connection, namespaceName, entryName) {
    return __awaiter(this, void 0, void 0, function* () {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const provider = new AnchorProvider(connection, null, {});
        const namespacesProgram = new Program(NAMESPACES_IDL, NAMESPACES_PROGRAM_ID, provider);
        const [namespaceId] = yield findNamespaceId(namespaceName);
        const [entryId] = yield findNameEntryId(namespaceId, entryName);
        const parsed = yield namespacesProgram.account.entry.fetch(entryId);
        return {
            parsed,
            pubkey: entryId,
        };
    });
}
export function getNameEntriesForNamespace(connection, namespaceName, entryNames) {
    return __awaiter(this, void 0, void 0, function* () {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const provider = new AnchorProvider(connection, null, {});
        const namespacesProgram = new Program(NAMESPACES_IDL, NAMESPACES_PROGRAM_ID, provider);
        const [namespaceId] = yield findNamespaceId(namespaceName);
        const entryTuples = yield Promise.all(entryNames.map((entryName) => findNameEntryId(namespaceId, entryName)));
        const entryIds = entryTuples.map((tuple) => tuple[0]);
        const result = (yield namespacesProgram.account.entry.fetchMultiple(entryIds));
        return result.map((parsed, i) => ({
            parsed,
            pubkey: entryIds[i],
            name: entryNames[i],
        }));
    });
}
export function getClaimRequest(connection, namespaceName, entryName, requestor) {
    return __awaiter(this, void 0, void 0, function* () {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const provider = new AnchorProvider(connection, null, {});
        const namespacesProgram = new Program(NAMESPACES_IDL, NAMESPACES_PROGRAM_ID, provider);
        const [namespaceId] = yield findNamespaceId(namespaceName);
        const [claimRequestId] = yield findClaimRequestId(namespaceId, entryName, requestor);
        const parsed = yield namespacesProgram.account.claimRequest.fetch(claimRequestId);
        return {
            parsed,
            pubkey: claimRequestId,
        };
    });
}
export function getPendingClaimRequests(connection) {
    return __awaiter(this, void 0, void 0, function* () {
        const programAccounts = yield connection.getProgramAccounts(NAMESPACES_PROGRAM_ID, {
            filters: [
                {
                    memcmp: {
                        offset: 0,
                        bytes: utils.bytes.bs58.encode(BorshAccountsCoder.accountDiscriminator("claimRequest")),
                    },
                },
            ],
        });
        const pendingClaimRequests = [];
        const coder = new BorshAccountsCoder(NAMESPACES_IDL);
        programAccounts.forEach((account) => {
            try {
                const claimRequest = coder.decode("claimRequest", account.account.data);
                if (!claimRequest.isApproved) {
                    pendingClaimRequests.push(Object.assign(Object.assign({}, account), { parsed: claimRequest }));
                }
            }
            catch (e) {
                console.log(`Failed to decode claim request`);
            }
        });
        return pendingClaimRequests;
    });
}
export function getReverseEntry(connection, pubkey) {
    return __awaiter(this, void 0, void 0, function* () {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const provider = new AnchorProvider(connection, null, {});
        const namespacesProgram = new Program(NAMESPACES_IDL, NAMESPACES_PROGRAM_ID, provider);
        const [reverseEntryId] = yield findReverseEntryId(pubkey);
        const parsed = yield namespacesProgram.account.reverseEntry.fetch(reverseEntryId);
        return {
            parsed,
            pubkey: reverseEntryId,
        };
    });
}
export function tryGetReverseEntry(connection, pubkey) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            return yield getReverseEntry(connection, pubkey);
        }
        catch (e) {
            console.log(`Failed to get reverse entry`);
            return null;
        }
    });
}
//# sourceMappingURL=accounts.js.map